import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaArrowLeft,
    FaCheck,
    FaClock,
    FaFileAlt,
    FaInfoCircle,
    FaMoneyBillWave,
    FaPaperPlane,
    FaReceipt,
    FaTimes,
    FaUser,
} from "react-icons/fa";
import { ExpenseDetails } from "../../models/expense/ExpenseDetail";
import { capitalizeWords } from "../../utils/string";
import { formatDateTime } from "../../utils/date";
import { sendRequest } from "../../utils/api";
import { ApproveExpenseModal, getStatusClasses, getStatusLabel, RejectExpenseModal } from "../../components/ExpenseComponent";
import FullPageSpinner from "../../components/FullPageSpinner";
import { getUser, hasAccess } from "../../utils/user-helper";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ConfirmModalContext";
import User from "../../models/User";
import FilePreviewModal from "../../Modal/AttachmentPreview";
import Attachment from "../../models/Attachment";
import { formatFileSize } from "../../utils/helper-function";
import ProfileAvatar from "../../components/ProfileAvatar";


export default function ExpenseDetailsPage() {
    const navigate = useNavigate();
    const { openModal } = useModal();
    const { showToast } = useToast();

    const { expenseId } = useParams();

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [actionAccess, setActionAccess] = useState<boolean>(false);
    const [exp, setExp] = useState({} as ExpenseDetails);
    const [user, setUser] = useState<User | null>({} as User);

    const [showApproval, setShowApproval] = useState(false);
    const [approvalAmount, setApprovalAmount] = useState<null | string>(null);
    const [remark, setRemark] = useState("");
    const [processing, setProcessing] = useState(false);

    const [selectedFile, setSelectedFile] = useState<{
        name: string;
        type: string;
        url: string;
    } | null>(null);

    useEffect(() => {
        const getExpense = async () => {
            try {
                setLoading(true);

                const response = await sendRequest(
                    "GET",
                    `/expenses/${expenseId}`
                );

                const result = await getUser();
                if (!result) return;


                const { user } = result;
                setUser(user);

                const permName = "expense_approve_reject";
                const permType = "action";

                const approver = response.approvers
                    .find((app: any) => app.approverId === user.id && app.status !== "pending");
                const access = await hasAccess(permName, permType) && !approver;

                setActionAccess(access);

                setExp(response);
            } catch (err) {
                console.error("Failed to fetch expense:", err);
            } finally {
                setLoading(false);
            }
        };

        getExpense();
    }, []);

    if (!exp) return null;

    const {
        items, attachments, site,
        expense, approvers, requestedBy,
    } = exp;


    const formatMoney = (amount?: number | null) => {
        if (amount === undefined || amount === null) {
            return "—";
        }

        return `₦${amount.toLocaleString("en-NG")}`;
    };

    const formatInputAmount = (value: string) => {
        const cleaned = value.replace(/[^0-9.]/g, "");

        if (!cleaned) return "";

        const parts = cleaned.split(".");

        const integerPart = parts[0];
        const decimalPart = parts[1];

        const formattedInteger = Number(integerPart).toLocaleString("en-NG");

        if (decimalPart !== undefined) {
            return `${formattedInteger}.${decimalPart.slice(0, 2)}`;
        }

        return formattedInteger;
    };

    const parseAmount = (value: string) => {
        return Number(value.replace(/,/g, "")) || 0;
    };

    const reductionAmount =
        expense?.requestedAmount - parseAmount(approvalAmount ?? "");

    const openApproval = () => {
        setApprovalAmount(
            expense?.requestedAmount.toLocaleString("en-NG")
        );

        setRemark("");
        setShowApproval(true);
    };

    const canApproverTakeAction = () => {
        const approver = approvers.find(
            (app) => app.approverId === user?.id
        );

        if (!approver) {
            alert("Access denied. Please contact the administrator.");
            return false;
        }

        const previousOrder = approver.order - 1;

        const previousApprover = approvers.find(
            (app) =>
                app.order === previousOrder &&
                app.status !== "approved"
        );

        if (previousApprover) {
            const approverName =
                `${previousApprover.firstName} ${previousApprover.lastName}`.trim();

            alert(
                `${approverName} (${previousApprover.portfolio}) must approve this expense before you can proceed.`
            );

            return false;
        }

        return true;
    };

    const handleApprove = async () => {
        if (!canApproverTakeAction()) {
            setShowApproval(false);
            return;
        }
        const amount = parseAmount(approvalAmount ?? "");

        if (!amount || amount <= 0) {
            alert("Please enter a valid approval amount.");
            return;
        }

        if (amount > expense?.requestedAmount) {
            alert("Approved amount cannot exceed the requested amount.");
            return;
        }

        if (amount < expense.requestedAmount && !remark.trim()) {
            alert("Please provide a reason for reducing the requested amount.");
            return;
        }

        setProcessing(true);

        try {

            const data = {
                remark,
                status: "approved",
                approvedAmount: amount,
            };

            await sendRequest(
                "PUT",
                `/expenses/${expense.id}/approve`,
                data
            );

            const approverId = user?.id ?? "unknown_id";

            setExp((prev) => ({
                ...prev,
                approvers: prev.approvers.map((app) =>
                    app.approverId === approverId
                        ? {
                            ...app,
                            remark: remark,
                            status: "approved",
                            approvedAmount: String(amount)
                        }
                        : app
                ),
            }));
            setActionAccess(false);
            showToast(
                amount < expense?.requestedAmount
                    ? `Expense partially approved at ${formatMoney(amount)}`
                    : `Expense approved at ${formatMoney(amount)}`,
                { type: "success" }
            );
        } catch (err: any) {
            if (err.status === 409) {
                return showToast("You have already taken action on this expense.", {
                    type: "error",
                });
            } else if (err.status === 403) {
                return showToast(err.response.data.error, {
                    type: "error",
                });
            }
        } finally {
            setShowApproval(false);
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!canApproverTakeAction()) {
            setShowRejectModal(false);
            return;
        }
        if (!remark.trim()) {
            alert("Please provide a reason for rejecting this expense.");
            return;
        }

        setProcessing(true);

        try {

            const data = {
                remark,
                status: "rejected",
            };

            await sendRequest(
                "PUT",
                `/expenses/${expense.id}/reject`, data
            );

            const approverId = user?.id ?? "unknown_id";

            setExp((prev) => ({
                ...prev,
                expense: {
                    ...prev.expense,
                    status: "rejected",
                },
                approvers: prev.approvers.map((app) =>
                    app.approverId === approverId
                        ? {
                            ...app,
                            remark: remark,
                            status: "rejected",
                        }
                        : app
                ),
            }));
            setActionAccess(false);
            setShowRejectModal(false);
            setRemark("");

            showToast("Expense request rejected.", { type: "success" });
        } catch (err: any) {
            if (err.status === 409) {
                return showToast("You have already taken action on this expense.", {
                    type: "error",
                });
            } else if (err.status === 403) {
                return showToast(err.response.data.error, {
                    type: "error",
                });
            }

        } finally {
            setShowRejectModal(false);
            setProcessing(false);
        }
    };

    const preveiwDocument = (file: any) => {
        setSelectedFile(file);
    }

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">
                <FullPageSpinner show={loading} />
                {/* Header */}
                <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-start gap-3">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                        >
                            {FaArrowLeft({
                                size: 13,
                                className: "",
                            })}
                        </button>

                        <div>
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-xl font-bold tracking-tight text-slate-800">
                                    Expense Details
                                </h1>

                                {/* <span className="text-xs text-slate-400">
                  #{expense.reference}
                </span> */}
                            </div>

                            <p className="mt-1 text-sm text-slate-500">
                                Review the expense request and its approval status.
                            </p>
                        </div>
                    </div>

                    {/* Admin action */}
                    {actionAccess && expense.status === "pending" && (
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={processing}
                                onClick={() => {
                                    setRemark("");
                                    setShowRejectModal(true);
                                }}
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-4 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                            >
                                {FaTimes({
                                    size: 11,
                                    className: "",
                                })}
                                Reject
                            </button>

                            <button
                                type="button"
                                disabled={processing}
                                onClick={openApproval}
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                            >
                                {FaCheck({
                                    size: 11,
                                    className: "",
                                })}
                                Review & Approve
                            </button>
                        </div>
                    )}
                </div>

                {/* Top summary */}
                <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    <SummaryCard
                        label="Requested Amount"
                        value={formatMoney(expense?.requestedAmount)}
                        icon={FaReceipt({
                            size: 15,
                            className: "",
                        })}
                    />

                    <SummaryCard
                        label="Approved Amount"
                        value={formatMoney(expense?.approvedAmount)}
                        icon={FaCheck({
                            size: 14,
                            className: "",
                        })}
                    />

                    {/* <SummaryCard
            label="Paid Amount"
            value={formatMoney(expense?.approvedAmount)}
            icon={FaPaperPlane({
              size: 14,
              className: "",
            })}
          /> */}

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                            Status
                        </p>

                        <div className="mt-3">
                            <span
                                className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold ${getStatusClasses(
                                    expense?.status
                                )}`}
                            >
                                {getStatusLabel(expense?.status)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
                    {/* Main content */}
                    <div className="space-y-6">
                        {/* Expense information */}
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
                                <h2 className="text-sm font-bold text-slate-800">
                                    Expense Information
                                </h2>
                            </div>

                            <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6">
                                <InfoField
                                    label="Expense Title"
                                    value={expense?.title}
                                />

                                <InfoField
                                    label="Category"
                                    value={expense?.category}
                                />

                                <InfoField
                                    label="Employee"
                                    value={`${requestedBy?.firstName} ${requestedBy?.lastName}`}
                                />

                                <InfoField
                                    label="Employee Role"
                                    value={capitalizeWords(requestedBy?.portfolio)}
                                />

                                <InfoField
                                    label="Request Date"
                                    value={formatDateTime(expense?.createdAt)}
                                />
                                {/* change one date to approver date or last action */}
                                <InfoField
                                    label="Last Approval Date"
                                    value={formatDateTime(expense?.updatedAt)}
                                />

                                <InfoField
                                    label="Project"
                                    value={capitalizeWords(site?.name)}
                                />

                                <InfoField
                                    label="Site / Location"
                                    value={site?.address || "Not specified"}
                                />

                                {expense?.description && <div className="sm:col-span-2">
                                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                        Business Justification
                                    </p>

                                    <div className="min-w-0 overflow-hidden rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
                                        {expense?.description}
                                    </div>
                                </div>}

                                {expense?.notes && (
                                    <div className="sm:col-span-2">
                                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                            Additional Notes
                                        </p>
                                        <div className="min-w-0 overflow-hidden rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
                                            {expense.notes}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Amount breakdown */}
                        {(items ?? []).length > 0 && <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-100 px-4 py-4 sm:px-6">
                                <h2 className="text-sm font-bold text-slate-800">
                                    Expense Breakdown
                                </h2>
                            </div>

                            {/* Mobile */}
                            <div className="divide-y divide-slate-100 sm:hidden">
                                {(items ?? []).map((item, index) => (
                                    <div
                                        key={item.id}
                                        className="min-w-0 px-4 py-4"
                                    >
                                        <div className="flex min-w-0 items-start gap-3">
                                            {/* Number */}
                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-[11px] font-semibold text-slate-400">
                                                {index + 1}
                                            </div>

                                            {/* Description */}
                                            <div className="min-w-0 flex-1">
                                                <p className="break-words text-sm font-medium leading-5 text-slate-700 [overflow-wrap:anywhere]">
                                                    {item.title}
                                                </p>

                                                <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                                    Amount
                                                </p>

                                                <p className="mt-0.5 text-sm font-bold text-slate-800">
                                                    {formatMoney(Number(item.amount))}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Mobile Total */}
                                <div className="flex items-center justify-between gap-4 bg-slate-50 px-4 py-4">
                                    <span className="text-xs font-semibold text-slate-500">
                                        Breakdown Total
                                    </span>

                                    <span className="shrink-0 text-base font-bold text-blue-600">
                                        {formatMoney(
                                            items.reduce((total, item) => total + Number(item.amount), 0)
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Desktop / Tablet */}
                            <div className="hidden overflow-x-auto sm:block">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-slate-100 bg-slate-50">
                                            <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                                #
                                            </th>

                                            <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                                Description
                                            </th>

                                            <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {(items ?? []).map((item, index) => (
                                            <tr
                                                key={item.id}
                                                className="border-b border-slate-50 last:border-0"
                                            >
                                                <td className="px-5 py-4 text-xs text-slate-400">
                                                    {index + 1}
                                                </td>

                                                <td className="break-words px-5 py-4 text-sm font-medium text-slate-700">
                                                    {item.title}
                                                </td>

                                                <td className="px-5 py-4 text-right text-sm font-semibold text-slate-800">
                                                    {formatMoney(Number(item.amount))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>

                                    <tfoot>
                                        <tr>
                                            <td
                                                colSpan={2}
                                                className="px-5 py-4 text-right text-xs font-semibold text-slate-500"
                                            >
                                                Breakdown Total
                                            </td>

                                            <td className="px-5 py-4 text-right text-base font-bold text-blue-600">
                                                {formatMoney(
                                                    items.reduce((total, item) => total + Number(item.amount), 0)
                                                )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </section>}

                        {/* Documents */}
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
                                <h2 className="text-sm font-bold text-slate-800">
                                    Supporting Documents
                                </h2>
                            </div>

                            <div className="space-y-2 p-5 sm:p-6">
                                {(attachments ?? []).length === 0 ? (
                                    <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center">
                                        <p className="text-xs text-slate-400">
                                            No documents attached.
                                        </p>
                                    </div>
                                ) : (
                                    attachments.map((document) => (
                                        <button
                                            type="button"
                                            key={document.id}
                                            className="flex w-full items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3 text-left transition hover:border-blue-100 hover:bg-blue-50"
                                        >
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                                                {FaFileAlt({
                                                    size: 15,
                                                    className: "",
                                                })}
                                            </div>

                                            <div className="w-0 min-w-0 flex-1 overflow-hidden">
                                                <p className="truncate text-xs font-semibold text-slate-700">
                                                    {document?.file?.name}
                                                </p>

                                                <p className="mt-1 truncate text-[10px] text-slate-400">
                                                    {document?.file?.type} · {formatFileSize(document?.file?.size)}
                                                </p>
                                            </div>

                                            <span
                                                onClick={() => preveiwDocument(document.file)}
                                                className="cursor-pointer text-[11px] font-semibold text-blue-600 hover:text-blue-800"
                                            >
                                                View
                                            </span>
                                        </button>
                                    ))
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Requester */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <h2 className="text-sm font-bold text-slate-800">
                                Requested By
                            </h2>

                            <div className="mt-4 flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                                    <ProfileAvatar
                                        photoUrl={requestedBy?.photoUrl}
                                        firstName={requestedBy?.firstName}
                                        className="border-2 border-white shadow-sm"
                                    />
                                </div>

                                <div>
                                    <p className="text-sm font-semibold text-slate-700">
                                        {requestedBy?.firstName} {requestedBy?.lastName}
                                    </p>

                                    <p className="mt-0.5 text-xs text-slate-400 capitalize">
                                        {requestedBy?.portfolio}
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Approval timeline */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-sm font-bold text-slate-800">
                                        Approval Progress
                                    </h2>

                                    <p className="mt-1 text-[11px] text-slate-400">
                                        Current request status
                                    </p>
                                </div>

                                {FaClock({
                                    size: 14,
                                    className: "text-slate-300",
                                })}
                            </div>

                            <div className="mt-5">
                                {(approvers ?? []).map((approval, index) => {
                                    const isLast = index === approvers.length - 1;
                                    const status = approval.status?.toLowerCase();

                                    const isApproved = status === "approved";
                                    const isRejected = status === "rejected";
                                    // const isPending = status === "pending";

                                    const adjustedAmount = approval.approvedAmount;
                                    const adjustmentReason = approval.remark;

                                    return (
                                        <div
                                            key={approval.id}
                                            className="relative flex gap-3"
                                        >
                                            {!isLast && (
                                                <div className="absolute left-[15px] top-8 h-[calc(100%-8px)] w-px bg-slate-200" />
                                            )}

                                            {/* Status icon */}
                                            <div
                                                className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isApproved
                                                    ? "bg-emerald-100 text-emerald-600"
                                                    : isRejected
                                                        ? "bg-red-100 text-red-600"
                                                        : "bg-slate-100 text-slate-400"
                                                    }`}
                                            >
                                                {isApproved ? (
                                                    FaCheck({ size: 11 })
                                                ) : isRejected ? (
                                                    FaTimes({ size: 11 })
                                                ) : (
                                                    FaClock({ size: 11 })
                                                )}
                                            </div>

                                            {/* Approver details */}
                                            <div className="min-w-0 flex-1 pb-6">
                                                <p className="text-xs font-semibold uppercase text-slate-700">
                                                    {approval.portfolio}
                                                </p>

                                                <p
                                                    className={`mt-1 text-[11px] font-medium ${isApproved
                                                        ? "text-emerald-600"
                                                        : isRejected
                                                            ? "text-red-600"
                                                            : "text-amber-600"
                                                        }`}
                                                >
                                                    {isApproved
                                                        ? "Approved"
                                                        : isRejected
                                                            ? "Rejected"
                                                            : "Pending approval"}
                                                </p>

                                                {/* Approved amount and adjustment reason */}
                                                {isApproved && (
                                                    <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50/60 p-3">
                                                        <p className="text-[10px] font-medium text-slate-500">
                                                            Approved Amount
                                                        </p>

                                                        <p className="mt-0.5 text-sm font-bold text-emerald-700">
                                                            {formatMoney(Number(adjustedAmount))}
                                                        </p>

                                                        {adjustmentReason && (
                                                            <div className="mt-2 border-t border-emerald-100 pt-2">
                                                                <p className="text-[10px] font-semibold text-slate-500">
                                                                    Reason for Adjustment
                                                                </p>

                                                                <p className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-5 text-slate-600">
                                                                    {adjustmentReason}
                                                                </p>
                                                            </div>
                                                        )}

                                                        <p className="mt-2 text-[10px] text-slate-400">
                                                            By {approval.firstName} {approval.lastName}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Rejection reason */}
                                                {isRejected && approval.remark && (
                                                    <div className="mt-2 rounded-lg border border-red-100 bg-red-50/60 p-3">
                                                        <p className="text-[10px] font-semibold text-red-600">
                                                            Rejection Reason
                                                        </p>
                                                        <p className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-5 text-slate-600">
                                                            {approval.remark}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Other remarks, if present */}
                                                {!isRejected && !isApproved && approval.remark && (
                                                    <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2">
                                                        <p className="text-[11px] leading-5 text-slate-500">
                                                            {approval.remark}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {actionAccess && expense.approvedAmount && expense.status === "approved" && (
                                <section className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h2 className="text-sm font-bold text-slate-800">
                                                Payment
                                            </h2>

                                            <p className="mt-1 text-[11px] text-slate-400">
                                                Record payment for this expense
                                            </p>
                                        </div>

                                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                                            {FaMoneyBillWave({
                                                size: 14,
                                                className: "",
                                            })}
                                        </div>
                                    </div>

                                    <div className="mt-4 rounded-xl bg-slate-50 p-4">
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                            Amount to Pay
                                        </p>

                                        <p className="mt-1 text-xl font-bold text-slate-800">
                                            {formatMoney(expense.approvedAmount ?? 0)}
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        //   onClick={() => setShowPayment(true)}
                                        className="mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 text-xs font-semibold text-white transition hover:bg-emerald-700"
                                    >
                                        {FaCheck({
                                            size: 11,
                                            className: "",
                                        })}
                                        Mark as Paid
                                    </button>
                                </section>
                            )}
                        </section>

                        {/* Admin approval card */}
                        {actionAccess && expense.status === "pending" && (
                            <section className="rounded-2xl border border-blue-100 bg-blue-50/60 p-5">
                                <div className="flex gap-3">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                                        {FaInfoCircle({
                                            size: 14,
                                            className: "",
                                        })}
                                    </div>

                                    <div>
                                        <h3 className="text-xs font-bold text-blue-900">
                                            Approval Required
                                        </h3>

                                        <p className="mt-1 text-[11px] leading-5 text-blue-700">
                                            This request was submitted for ₦
                                            {expense.requestedAmount.toLocaleString("en-NG")}.
                                            You may approve the full amount or a lower amount.
                                        </p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={openApproval}
                                    className="mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-xs font-semibold text-white transition hover:bg-blue-700"
                                >
                                    {FaCheck({
                                        size: 11,
                                        className: "",
                                    })}
                                    Review Request
                                </button>
                            </section>
                        )}
                    </div>
                </div>
            </div>

            {/* Approval Modal */}
            <ApproveExpenseModal
                isOpen={showApproval && !!actionAccess}
                expense={expense}
                approvalAmount={approvalAmount as string}
                approvalComment={remark}
                reductionAmount={reductionAmount}
                processing={processing}
                formatMoney={formatMoney}
                formatInputAmount={formatInputAmount}
                parseAmount={parseAmount}
                onAmountChange={setApprovalAmount}
                onCommentChange={setRemark}
                onCancel={() => setShowApproval(false)}
                onApprove={handleApprove}
            />

            <FilePreviewModal
                file={selectedFile}
                onClose={() => setSelectedFile(null)}
            />

            {/* Rejection Modal */}
            <RejectExpenseModal
                isOpen={showRejectModal && !!actionAccess}
                expense={expense}
                rejectionReason={remark}
                processing={processing}
                formatMoney={formatMoney}
                onReasonChange={setRemark}
                onCancel={() => setShowRejectModal(false)}
                onReject={handleReject}
            />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Components                                                                 */
/* -------------------------------------------------------------------------- */

interface SummaryCardProps {
    label: string;
    value: string;
    icon: React.ReactNode;
}

function SummaryCard({
    label,
    value,
    icon,
}: SummaryCardProps) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    {label}
                </p>

                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    {icon}
                </div>
            </div>

            <p className="mt-3 text-xl font-bold tracking-tight text-slate-800">
                {value}
            </p>
        </div>
    );
}

interface InfoFieldProps {
    label: string;
    value: string;
}

function InfoField({ label, value }: InfoFieldProps) {
    return (
        <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {label}
            </p>

            <p className="text-sm font-medium text-slate-700">
                {value}
            </p>
        </div>
    );
}